import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useAuth } from '../contexts/AuthContext';
import { useTenantTheme } from '../contexts/TenantThemeContext';
import api from '../services/api';
import SafeImage from '../components/SafeImage';
import { Sheet, SheetContent } from '../components/ui/sheet';
import SupportMenu from './player/components/SupportMenu';
import { tenantSlugFromHost } from '../utils/tenantDomains';
import {
  Crown, MessageCircle, Send, ChevronRight, ArrowRight, Trophy, Gem,
  X, Sparkles, Users, Wallet, Mail, Phone, BadgeCheck, Link2, Instagram,
  Youtube, Facebook, Globe, Menu,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const SOCIAL_DEFS = [
  { key: 'instagram', label: 'Instagram', icon: Instagram, cta: 'Follow', color: '#E1306C', bg: 'linear-gradient(135deg, #833AB4, #E1306C 50%, #FCB045 100%)' },
  { key: 'youtube', label: 'YouTube', icon: Youtube, cta: 'Subscribe', color: '#EF4444', bg: '#FF0000' },
  { key: 'tiktok', label: 'TikTok', icon: null, cta: 'Follow', color: '#22FF88', bg: '#000000' },
  { key: 'facebook', label: 'Facebook', icon: Facebook, cta: 'Message', color: '#3B82F6', bg: '#1877F2' },
  { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, cta: 'Join', color: '#22C55E', bg: '#25D366' },
  { key: 'discord', label: 'Discord', icon: MessageCircle, cta: 'Join', color: '#8B5CF6', bg: '#5865F2' },
  { key: 'telegram', label: 'Telegram', icon: Send, cta: 'Open', color: '#06B6D4', bg: '#26A5E4' },
  { key: 'website', label: 'Website', icon: Globe, cta: 'Visit', color: '#22FF88', bg: 'linear-gradient(135deg, #22FF88, #00C853)' },
];

function normalizeLink(value, kind) {
  let raw = String(value || '').trim();
  if (!raw) return '';
  const embedded = raw.match(/https?:\/\/[^\s]+/i);
  if (embedded) raw = embedded[0].replace(/[),.;]+$/, '');
  if (kind === 'email') return raw.includes('@') ? raw : '';
  if (kind === 'phone') return raw;
  if (/^https?:\/\//i.test(raw)) return raw;
  if (kind === 'telegram') return `https://t.me/${raw.replace(/^@/, '').replace(/^t\.me\//i, '')}`;
  if (kind === 'whatsapp') {
    if (/^(wa\.me|api\.whatsapp\.com|chat\.whatsapp\.com|whatsapp\.com)\//i.test(raw)) return `https://${raw}`;
    return `https://wa.me/${raw.replace(/[^\d+]/g, '').replace(/^\+/, '')}`;
  }
  if (kind === 'messenger') return `https://m.me/${raw.replace(/^m\.me\//i, '').replace(/^@/, '')}`;
  return `https://${raw}`;
}

/**
 * LandingPage (Phase 25r — Feb 2026)
 * ----------------------------------
 * - 3-banner auto-sliding promo carousel (Sign-up bonus / Referral / Crypto deposit)
 * - Single big "Play Now" button under the carousel — pulses/breathes
 *   to invite the click. Routes to /app for logged-in users, /auth otherwise.
 * - Floating chat-support FAB mirrors the player-home support button.
 * - Clean footer: centered logo + social channels (FB/WA/TG, admin-set)
 *   + Privacy / Terms + © year.
 */
function getTenantHostSlug() {
  return tenantSlugFromHost();
}

export default function LandingPage({ tenantSlugOverride = null }) {
  const { user, tenantSlug: authTenantSlug } = useAuth();
  const navigate = useNavigate();
  const { tenantSlug: paramTenantSlug } = useParams();
  const { theme, tenantSlug: themedTenantSlug } = useTenantTheme();
  const hostTenantSlug = getTenantHostSlug();
  const tenantSlug = tenantSlugOverride || paramTenantSlug || authTenantSlug || themedTenantSlug || hostTenantSlug;
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);
  const [branding, setBranding] = useState({});
  const [activeBanner, setActiveBanner] = useState(0);
  const [paused, setPaused] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [brandOpen, setBrandOpen] = useState(false);
  const [landingBanners, setLandingBanners] = useState([]);
  const [publicSupportChannels, setPublicSupportChannels] = useState({});
  const touch = useRef({ x: 0, y: 0, t: 0 });

  useEffect(() => { fetchData(); }, [tenantSlug]);

  useEffect(() => {
    api.get('/api/branding').then(r => setBranding(r.data || {})).catch(() => {});
  }, [tenantSlug]);

  useEffect(() => {
    api.get('/api/public/settings/support-channels')
      .then(r => setPublicSupportChannels(r.data?.support_channels || {}))
      .catch(() => setPublicSupportChannels({}));
  }, [tenantSlug]);

  useEffect(() => {
    let live = true;
    api.get('/api/promo-banners', {
      params: { placement: 'landing', _refresh: Date.now() },
      headers: { 'Cache-Control': 'no-cache' },
    })
      .then((r) => {
        if (!live) return;
        const list = (r.data?.banners || [])
          .filter((b) => b.enabled !== false && b.image_url)
          .map((b) => ({ ...b, id: b.id || `landing-${b.image_url}`, type: 'uploaded' }));
        setLandingBanners(list);
        setActiveBanner(0);
      })
      .catch(() => {
        if (live) setLandingBanners([]);
      });
    return () => { live = false; };
  }, [tenantSlug]);

  const fetchData = async () => {
    try {
      const promises = [api.get('/api/public/games').catch(() => ({ data: { games: [] } }))];
      if (tenantSlug) promises.push(api.get(`/api/tenants/public/${tenantSlug}`).catch(() => ({ data: null })));
      const [gRes, tRes] = await Promise.all(promises);
      setGames(gRes.data.games || gRes.data || []);
      if (tRes?.data) setTenant(tRes.data);
    } catch {} finally { setLoading(false); }
  };

  const resolveUrl = (url) => {
    if (!url) return '';
    if (/^(https?:|data:|blob:)/i.test(url)) return url;
    return `${API_BASE}${url}`;
  };

  const brandName = theme?.brand_name || tenant?.name || branding.brand_name || 'TeamLobby';
  const brandColor = theme?.btn_primary_from || theme?.neon || tenant?.branding?.primary_color || branding.primary_color || '#D4AF37';
  const brandColor2 = theme?.btn_primary_to || tenant?.branding?.secondary_color || '#F5D76E';
  const brandLogo = theme?.logo_url || theme?.intro_logo_url || tenant?.branding?.logo_url || branding.logo_url;
  // Social and support contact details are tenant-owned.  A tenant page must
  // never inherit the platform-wide channels: otherwise an unconfigured
  // tenant can accidentally send its players to another team's accounts.
  const tenantScoped = Boolean(tenantSlug);
  const tenantBranding = tenant?.branding || {};
  const support = tenantScoped ? (tenantBranding.support || {}) : (branding.support || {});
  const socials = tenantScoped ? (tenantBranding.socials || {}) : (branding.socials || {});
  const platformSupportChannels = tenantScoped ? {} : publicSupportChannels;
  const supportLinks = {
    ...support,
    telegram_url: normalizeLink(support.telegram_url, 'telegram') || normalizeLink(platformSupportChannels.telegram, 'telegram') || normalizeLink(socials.telegram, 'telegram') || '',
    whatsapp_url: normalizeLink(support.whatsapp_url, 'whatsapp') || normalizeLink(platformSupportChannels.whatsapp, 'whatsapp') || normalizeLink(socials.whatsapp, 'whatsapp') || '',
    messenger_url: support.messenger_url || normalizeLink(platformSupportChannels.messenger, 'messenger') || '',
    discord_url: support.discord_url || socials.discord || '',
    email: support.email || socials.support_email || '',
    phone: support.phone || '',
    facebook_url: normalizeLink(support.facebook_url, 'facebook') || normalizeLink(platformSupportChannels.facebook, 'facebook') || normalizeLink(socials.facebook, 'facebook') || '',
  };
  const facebookUrl = supportLinks.facebook_url;
  const whatsappUrl = supportLinks.whatsapp_url;
  const telegramUrl = supportLinks.telegram_url;
  const brandSocials = {
    ...socials,
    instagram: socials.instagram || support.instagram || '',
    youtube: socials.youtube || support.youtube || '',
    tiktok: socials.tiktok || support.tiktok || '',
    facebook: socials.facebook || supportLinks.facebook_url,
    whatsapp: socials.whatsapp || supportLinks.whatsapp_url,
    telegram: socials.telegram || supportLinks.telegram_url,
    discord: socials.discord || supportLinks.discord_url,
    support_email: socials.support_email || supportLinks.email,
  };
  const pathPrefix = hostTenantSlug ? '' : (tenantSlug ? `/${tenantSlug}` : '');
  const authPath = `${pathPrefix}/auth`;
  const appPath = `${pathPrefix}/app`;
  const adminPath = `${pathPrefix}/admin`;
  const supportMenuBranding = {
    ...branding,
    socials,
    logo_url: brandLogo || branding.logo_url,
    intro_logo_url: brandLogo || branding.intro_logo_url,
    support: {
      ...support,
      ...brandSocials,
      facebook_url: supportLinks.facebook_url,
      telegram_url: supportLinks.telegram_url,
      whatsapp_url: supportLinks.whatsapp_url,
      discord_url: supportLinks.discord_url,
      messenger_url: supportLinks.messenger_url,
      support_email: supportLinks.email,
      email: supportLinks.email,
    },
  };

  // Smart Play Now: route to app if authenticated, else signup
  const goPlay = () => {
    if (user) {
      navigate(user.role === 'player' ? appPath : adminPath);
    } else {
      navigate(authPath);
    }
  };

  const goAuthFromSupport = () => {
    setSupportOpen(false);
    setTimeout(() => navigate(authPath), 120);
  };

  const goMenu = () => {
    if (user) {
      navigate(user.role === 'player' ? appPath : adminPath);
    } else {
      navigate(authPath);
    }
  };

  // ─────────────────────────── Banners ───────────────────────────
  // Each banner reads its accent + bg from the tenant theme so admins
  // can re-skin the landing page from the Theme Editor.
  const t = theme || {};
  const BANNERS = [
    {
      id: 'signup-bonus',
      eyebrow: 'WELCOME OFFER',
      title: 'Sign up & get',
      highlight: '$5 FreePlay',
      body: 'New player bonus — instant credit on signup',
      cta: 'Claim now',
      icon: Sparkles,
      accent: t.landing_banner1_accent || '#22FF88',
      accentDeep: t.landing_banner1_accent_deep || '#065F46',
      bg: t.landing_banner1_bg || 'linear-gradient(135deg, #052E1A 0%, #064E3B 50%, #10B981 100%)',
      type: 'signup',
    },
    {
      id: 'refer-earn',
      eyebrow: 'REFER & EARN',
      title: 'Invite a friend ·',
      highlight: '$5 FP + 50% of first deposit',
      body: 'Earn rewards every time your friend deposits',
      cta: 'Invite & earn',
      icon: Users,
      accent: t.landing_banner2_accent || '#A78BFA',
      accentDeep: t.landing_banner2_accent_deep || '#6D28D9',
      bg: t.landing_banner2_bg || 'linear-gradient(135deg, #2E1065 0%, #4C1D95 50%, #6D28D9 100%)',
      type: 'refer',
    },
    {
      id: 'deposit-bonus',
      eyebrow: 'FIRST DEPOSIT',
      title: '25% bonus on',
      highlight: 'your first deposit',
      body: 'All major payment methods supported',
      cta: 'Deposit & play',
      icon: Wallet,
      accent: t.landing_banner3_accent || '#F59E0B',
      accentDeep: t.landing_banner3_accent_deep || '#B45309',
      bg: t.landing_banner3_bg || 'linear-gradient(135deg, #451A03 0%, #92400E 50%, #F59E0B 110%)',
      type: 'deposit',
      showPayments: true,
    },
  ];
  const heroSlides = landingBanners.length ? landingBanners : BANNERS;

  // Auto-slide
  useEffect(() => {
    if (paused) return undefined;
    if (heroSlides.length < 2) return undefined;
    const t = setInterval(() => setActiveBanner((i) => (i + 1) % heroSlides.length), 6000);
    return () => clearInterval(t);
  }, [paused, heroSlides.length]);

  const pauseFor = (ms = 6000) => {
    setPaused(true);
    if (pauseFor.t) clearTimeout(pauseFor.t);
    pauseFor.t = setTimeout(() => setPaused(false), ms);
  };

  const onTouchStart = (e) => {
    const t0 = e.touches?.[0];
    if (!t0) return;
    touch.current = { x: t0.clientX, y: t0.clientY, t: Date.now() };
    pauseFor(7000);
  };
  const onTouchEnd = (e) => {
    const t1 = e.changedTouches?.[0];
    if (!t1) return;
    const dx = t1.clientX - touch.current.x;
    const dy = t1.clientY - touch.current.y;
    const dt = Date.now() - touch.current.t;
    if (dt > 600 || Math.abs(dx) < 50 || Math.abs(dy) > Math.abs(dx)) return;
    setActiveBanner((i) => (dx < 0 ? (i + 1) % heroSlides.length : (i - 1 + heroSlides.length) % heroSlides.length));
  };

  const supportEnabled = supportLinks.enabled !== false;

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B0E14' }}><div className="spinner" /></div>;

  const playFrom = '#F5D76E';
  const playTo = '#D99012';
  const playText = '#130A00';
  const pageBg = t.landing_bg || 'linear-gradient(180deg, #02070C 0%, #071725 48%, #041017 100%)';

  return (
    <div className="flex min-h-[100dvh] flex-col overflow-x-hidden" style={{ background: pageBg }} data-testid="landing-page">
      {/* Header */}
      <header
        className="fixed left-0 right-0 top-0 z-50 px-4 pb-3 pt-3"
        style={{
          paddingTop: 'calc(0.75rem + env(safe-area-inset-top))',
          paddingLeft: 'max(1rem, env(safe-area-inset-left))',
          paddingRight: 'max(1rem, env(safe-area-inset-right))',
          background: 'rgba(2,7,12,0.9)',
          backdropFilter: 'blur(16px)',
          borderBottom: `1px solid ${brandColor}20`,
        }}
      >
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setBrandOpen(true)}
            className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden rounded-2xl pr-1 transition active:scale-[0.99] hover:brightness-110 sm:gap-3 sm:pr-2"
            data-testid="landing-logo-socials"
            aria-label={`Open ${brandName} social links`}
          >
            {brandLogo ? (
              <span
                className="w-11 h-11 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center overflow-hidden shrink-0"
                style={{
                  background: 'rgba(0,0,0,0.35)',
                  border: `1px solid ${brandColor2}66`,
                  boxShadow: `0 0 18px ${brandColor2}55`,
                }}
              >
                <SafeImage
                  src={resolveUrl(brandLogo)}
                  alt={brandName}
                  className="w-full h-full object-contain p-0.5"
                  loading="eager"
                  fetchPriority="high"
                  fallback={<Crown className="w-5 h-5 text-white/80" />}
                />
              </span>
            ) : (
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `linear-gradient(135deg, ${brandColor}, ${brandColor2})`, boxShadow: `0 0 18px ${brandColor2}55` }}>
                <Crown className="w-5 h-5 text-black" />
              </div>
            )}
            <span
              className="block min-w-0 truncate whitespace-nowrap font-black uppercase leading-tight tracking-wide"
              style={{
                color: '#F5D76E',
                fontSize: 'clamp(12px, 4vw, 24px)',
                maxWidth: 'calc(100vw - 9.25rem)',
                textShadow: '0 1px 0 rgba(0,0,0,0.6), 0 0 18px rgba(245,215,110,0.35)',
              }}
              title={brandName}
            >
              {brandName}
            </span>
            <BadgeCheck className="w-4 h-4 text-sky-400 shrink-0" />
          </button>

          <div className="relative shrink-0">
            <button
              type="button"
              onClick={goMenu}
              className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center transition active:scale-95 hover:brightness-110"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: `1px solid ${brandColor}24`,
                boxShadow: '0 12px 28px rgba(0,0,0,0.28)',
              }}
              data-testid="landing-menu-btn"
              aria-label="Open menu"
            >
              <Menu className="w-7 h-7" style={{ color: brandColor }} />
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1" style={{ paddingTop: 'calc(6rem + env(safe-area-inset-top))' }}>
        {/* Hero — 3-banner auto-sliding carousel */}
        <section className="px-3 sm:px-4 pt-2" data-testid="landing-hero-banners">
          <div className="max-w-5xl mx-auto">
            <div
              className="relative overflow-hidden rounded-[28px]"
              onTouchStart={onTouchStart}
              onTouchEnd={onTouchEnd}
            >
              <div
                className="flex transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${activeBanner * 100}%)` }}
              >
                {heroSlides.map((b, i) => (
                  <BannerCard
                    key={b.id}
                    banner={b}
                    isActive={i === activeBanner}
                    onClick={goPlay}
                    resolveImage={resolveUrl}
                  />
                ))}
              </div>
            </div>
            {/* dots */}
            <div className="flex items-center justify-center gap-1.5 pt-3">
              {heroSlides.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => { pauseFor(7000); setActiveBanner(i); }}
                  className="rounded-full transition-all"
                  style={{
                    width: i === activeBanner ? '26px' : '8px',
                    height: '8px',
                    background: i === activeBanner ? '#FFFFFF' : 'rgba(255,255,255,0.35)',
                  }}
                  aria-label={`Go to banner ${i + 1}`}
                  data-testid={`landing-banner-dot-${i}`}
                />
              ))}
            </div>
          </div>

          {/* Big "Play Now" — alive & calling */}
          <div className="max-w-5xl mx-auto mt-6 sm:mt-8 flex justify-center">
            <button
              onClick={goPlay}
              className="play-now-cta relative inline-flex items-center justify-center px-12 sm:px-16 py-5 sm:py-6 rounded-full font-black text-xl sm:text-2xl tracking-wide active:scale-95 transition"
              style={{
                background: `linear-gradient(135deg, ${playFrom} 0%, ${playTo} 100%)`,
                color: playText,
                boxShadow: `0 18px 48px ${playFrom}88, 0 0 0 1px rgba(255,255,255,0.4) inset`,
              }}
              data-testid="play-now-cta"
              aria-label="Play now"
            >
              <span aria-hidden className="play-now-glow" style={{ background: 'radial-gradient(circle, rgba(245,215,110,0.58) 0%, rgba(217,144,18,0.28) 38%, transparent 72%)' }} />
              <span aria-hidden className="play-now-ring" style={{ borderColor: playFrom }} />
              <span aria-hidden className="play-now-ring play-now-ring-d2" style={{ borderColor: playTo }} />
              <span className="relative">Play Now</span>
            </button>
          </div>

        </section>

        {/* Games Section */}
        <section className="py-5 px-4 mt-0" style={{ background: 'rgba(255,255,255,0.01)' }}>
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-x-3 gap-y-5 sm:gap-x-5 sm:gap-y-6">
              {games.map((game, idx) => {
                const img = game.logo_image_url || game.logo_url || game.image || game.image_url || '';
                const imgFull = img && img.startsWith('/') ? `${API_BASE}${img}` : img;
                return (
                  <div key={game.id} onClick={goPlay} className="cursor-pointer group w-full max-w-[7.5rem] sm:max-w-[10rem] md:max-w-none mx-auto"
                    data-testid={`landing-game-${game.id}`}>
                    <div className="aspect-square rounded-2xl overflow-hidden mb-1.5 transition-all duration-200 group-hover:brightness-110"
                      style={{
                        background: 'linear-gradient(135deg, rgba(2,7,12,0.96), rgba(22,34,45,0.9))',
                        boxShadow: '0 0 20px rgba(245,215,110,0.22), 0 8px 20px rgba(0,0,0,0.45)',
                        border: '1px solid rgba(245,215,110,0.26)',
                      }}>
                      {img ? (
                        <SafeImage
                          src={imgFull}
                          alt={game.name}
                          className="w-full h-full object-contain p-1"
                          loading="lazy"
                          fallback={<div className="w-full h-full flex items-center justify-center text-2xl font-bold" style={{ background: 'linear-gradient(135deg, #1e1245, #0d1117)', color: brandColor }}>{game.name?.charAt(0)}</div>}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-2xl font-bold" style={{ background: 'linear-gradient(135deg, #1e1245, #0d1117)', color: brandColor }}>{game.name?.charAt(0)}</div>
                      )}
                    </div>
                    <p className="min-h-[1.8rem] px-0.5 text-xs sm:text-[15px] font-extrabold text-center leading-tight text-slate-100 drop-shadow-sm group-hover:text-white transition">{game.name}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </main>

      {/* Footer — clean: logo + socials + Privacy/Terms + © */}
      <footer className="px-4 py-10 mt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)', background: 'rgba(0,0,0,0.25)' }} data-testid="landing-footer">
        <div className="max-w-3xl mx-auto flex flex-col items-center gap-6">
          {/* Logo (large, dynamic) */}
          <button
            type="button"
            onClick={() => setBrandOpen(true)}
            className="flex flex-col items-center gap-2 transition active:scale-[0.99] hover:brightness-110"
            data-testid="landing-footer-logo"
            aria-label={`Open ${brandName} social links`}
          >
            {brandLogo ? (
              <SafeImage
                src={resolveUrl(brandLogo)}
                alt={brandName}
                className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover"
                style={{ boxShadow: `0 10px 30px ${brandColor}33` }}
                loading="lazy"
                fallback={(
                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${brandColor}, ${brandColor2})`, boxShadow: `0 10px 30px ${brandColor}55` }}>
                    <Crown className="w-10 h-10 sm:w-12 sm:h-12 text-black" />
                  </div>
                )}
              />
            ) : (
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${brandColor}, ${brandColor2})`, boxShadow: `0 10px 30px ${brandColor}55` }}>
                <Crown className="w-10 h-10 sm:w-12 sm:h-12 text-black" />
              </div>
            )}
            <span className="text-base font-bold" style={{ background: `linear-gradient(90deg, ${brandColor}, ${brandColor2})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{brandName}</span>
          </button>

          {/* Social row — only those configured by admin */}
          {(facebookUrl || whatsappUrl || telegramUrl) && (
            <div className="flex items-center gap-4" data-testid="landing-footer-socials">
              {facebookUrl && (
                <a href={facebookUrl} target="_blank" rel="noreferrer" className="w-11 h-11 rounded-full flex items-center justify-center transition active:scale-95 hover:scale-105" style={{ background: 'rgba(24, 119, 242, 0.15)', border: '1px solid rgba(24, 119, 242, 0.4)' }} data-testid="footer-facebook" aria-label="Facebook">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
              )}
              {whatsappUrl && (
                <a href={whatsappUrl} target="_blank" rel="noreferrer" className="w-11 h-11 rounded-full flex items-center justify-center transition active:scale-95 hover:scale-105" style={{ background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.4)' }} data-testid="footer-whatsapp" aria-label="WhatsApp">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#22c55e"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/></svg>
                </a>
              )}
              {telegramUrl && (
                <a href={telegramUrl} target="_blank" rel="noreferrer" className="w-11 h-11 rounded-full flex items-center justify-center transition active:scale-95 hover:scale-105" style={{ background: 'rgba(56,189,248,0.15)', border: '1px solid rgba(56,189,248,0.4)' }} data-testid="footer-telegram" aria-label="Telegram">
                  <Send className="w-5 h-5 text-[#38BDF8]" />
                </a>
              )}
            </div>
          )}

          {/* Privacy / Terms */}
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-center text-xs font-semibold uppercase tracking-wider text-gray-400 sm:gap-x-6" data-testid="landing-footer-legal">
            <button onClick={() => navigate('/privacy')} className="hover:text-white transition" data-testid="footer-privacy">Privacy Policy</button>
            <span className="text-white/20">·</span>
            <button onClick={() => navigate('/terms')} className="hover:text-white transition" data-testid="footer-terms">Terms &amp; Conditions</button>
          </div>

          {/* Copyright */}
          <p className="break-words text-center text-xs text-gray-500" data-testid="landing-footer-copy">
            © {new Date().getFullYear()} {brandName}. All Rights Reserved.
          </p>
        </div>
      </footer>

      {/* Floating chat support FAB — same style as player home */}
      {supportEnabled && (
        <button
          onClick={() => setSupportOpen(true)}
          className="landing-support-fab support-fab-anim support-fab-clean fixed z-40 rounded-full flex items-center justify-center transition active:scale-95 group"
          style={{
            right: 'max(14px, env(safe-area-inset-right))',
            bottom: 'calc(22px + env(safe-area-inset-bottom))',
          }}
          data-testid="landing-support-fab"
          aria-label="Open support"
        >
          <span aria-hidden className="support-fab-ripple" />
          <span aria-hidden className="support-fab-ripple support-fab-ripple-delay" />
          <svg
            viewBox="0 0 24 24"
            className="w-7 h-7 support-fab-icon"
            style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.45))' }}
            aria-hidden
            focusable="false"
          >
            <path
              fill="#FFFFFF"
              d="M12 2C6.48 2 2 6.14 2 11.25c0 2.86 1.4 5.42 3.6 7.13V22l3.3-1.81c.99.27 2.04.42 3.1.42 5.52 0 10-4.14 10-9.36S17.52 2 12 2zm1.05 12.55l-2.55-2.72-4.97 2.72 5.46-5.79 2.62 2.72 4.9-2.72-5.46 5.79z"
            />
          </svg>
        </button>
      )}

      {/* Support sheet — mirrors the signed-in player support menu. */}
      <Sheet open={supportOpen} onOpenChange={setSupportOpen}>
        <SheetContent
          side="right"
          className="player-app p-0 border-l w-full sm:max-w-sm"
          style={{
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
            background: 'var(--vl-bg, #000000)',
            borderColor: 'rgba(34,255,136,0.3)',
          }}
        >
          <SupportMenu
            branding={supportMenuBranding}
            chatUnread={0}
            agentUnread={[]}
            onPickAgent={goAuthFromSupport}
            onLiveChat={goAuthFromSupport}
            onOpenChannel={goAuthFromSupport}
            onManagePreferred={goAuthFromSupport}
            onClose={() => setSupportOpen(false)}
          />
        </SheetContent>
      </Sheet>

      {brandOpen && (
        <BrandSocialOverlay
          brandName={brandName}
          brandLogo={brandLogo}
          brandColor={brandColor}
          brandColor2={brandColor2}
          branding={{ ...branding, socials: brandSocials }}
          resolveUrl={resolveUrl}
          onClose={() => setBrandOpen(false)}
        />
      )}

      {/* keyframes — Play Now alive ring + glow + FAB ripple */}
      <style>{`
        @keyframes play-now-pulse {
          0%, 100% { transform: scale(1);   filter: brightness(1.0); }
          50%      { transform: scale(1.05); filter: brightness(1.18); }
        }
        @keyframes play-now-glow-pulse {
          0%, 100% { opacity: 0.5; transform: scale(0.92); }
          50%      { opacity: 0.95; transform: scale(1.18); }
        }
        @keyframes play-now-ring-expand {
          0%   { opacity: 0.85; transform: scale(0.95); }
          70%  { opacity: 0;    transform: scale(1.45); }
          100% { opacity: 0;    transform: scale(1.45); }
        }
        .play-now-cta { animation: play-now-pulse 2.4s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
        .play-now-cta .play-now-glow {
          position: absolute; inset: -30%; border-radius: 9999px; pointer-events: none;
          animation: play-now-glow-pulse 2.4s ease-in-out infinite;
          z-index: 0;
        }
        .play-now-cta .play-now-ring {
          position: absolute; inset: 0; border-radius: 9999px; border-width: 2px; border-style: solid;
          pointer-events: none; z-index: 0;
          animation: play-now-ring-expand 2.4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        .play-now-cta .play-now-ring-d2 { animation-delay: 1.2s; }
        @media (prefers-reduced-motion: reduce) {
          .play-now-cta, .play-now-cta .play-now-glow, .play-now-cta .play-now-ring { animation: none !important; }
        }
      `}</style>
    </div>
  );
}

// ─────────────────────────── Banner card ───────────────────────────
function BannerCard({ banner, isActive, onClick, resolveImage }) {
  const Icon = banner.icon || Sparkles;
  const imageUrl = banner.image_url && resolveImage ? resolveImage(banner.image_url) : '';
  if (imageUrl) {
    const canRedirect = banner.redirect_enabled !== false;
    return (
      <button
        type="button"
        onClick={canRedirect ? onClick : undefined}
        disabled={!canRedirect}
        data-testid={`landing-banner-${banner.id}`}
        data-active={isActive ? 'true' : 'false'}
        className={`shrink-0 w-full text-left rounded-[28px] overflow-hidden relative transition ${canRedirect ? 'active:scale-[0.99]' : 'cursor-default'}`}
        style={{
          background: 'rgba(2,7,12,0.72)',
          border: `1.5px solid ${(banner.accent || '#22FF88')}55`,
          boxShadow: `0 20px 44px rgba(0,0,0,0.55), 0 0 34px ${(banner.accent || '#22FF88')}44`,
          aspectRatio: '1600 / 760',
        }}
        aria-label={banner.title || 'Promo banner'}
      >
        <span
          className="absolute inset-0 scale-110 opacity-30 blur-xl"
          style={{
            backgroundImage: `url("${imageUrl}")`,
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
          }}
          aria-hidden
        />
        <SafeImage
          src={imageUrl}
          alt={banner.title || 'Promo banner'}
          className="relative z-10 w-full h-full object-contain"
          loading="eager"
          fetchPriority="high"
          fallback={<div className="relative z-10 w-full h-full" style={{ background: 'rgba(2,7,12,0.8)' }} />}
        />
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`landing-banner-${banner.id}`}
      data-active={isActive ? 'true' : 'false'}
      className="shrink-0 w-full text-left rounded-[28px] overflow-hidden relative transition active:scale-[0.99]"
      style={{
        background: banner.bg,
        border: `1.5px solid ${banner.accent}66`,
        boxShadow: `0 16px 36px rgba(0,0,0,0.5), 0 0 0 1px ${banner.accent}33, 0 0 30px ${banner.accent}88`,
      }}
    >
      {/* Floating sparkles */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
        {[
          { l: '12%', t: '20%', d: '0s' },
          { l: '78%', t: '16%', d: '0.6s' },
          { l: '65%', t: '78%', d: '1.1s' },
          { l: '20%', t: '74%', d: '0.3s' },
        ].map((p, k) => (
          <span key={k} className="absolute rounded-full" style={{
            left: p.l, top: p.t, width: 4, height: 4,
            background: '#FFFFFF',
            boxShadow: `0 0 10px ${banner.accent}`,
            animation: `landing-sparkle 2.4s ease-in-out infinite ${p.d}`,
            opacity: 0.55,
          }} />
        ))}
      </div>
      <span className="pointer-events-none absolute inset-0" style={{ background: 'linear-gradient(110deg, transparent 35%, rgba(255,255,255,0.18) 50%, transparent 65%)', animation: 'landing-shimmer 4s ease-in-out infinite' }} aria-hidden />

      <div className="relative px-5 py-5 sm:px-7 sm:py-6 flex items-center gap-4 min-h-[160px] sm:min-h-[200px]">
        <div className="relative shrink-0">
          <div className="absolute inset-0 rounded-2xl blur-xl" style={{ background: banner.accent, opacity: 0.55 }} aria-hidden />
          <div
            className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center"
            style={{
              background: `linear-gradient(135deg, ${banner.accent} 0%, ${banner.accentDeep} 100%)`,
              boxShadow: `0 10px 22px ${banner.accent}88, inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -2px 4px rgba(0,0,0,0.25)`,
            }}
          >
            <Icon className="w-8 h-8 sm:w-10 sm:h-10 text-white" strokeWidth={2.4} />
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-bold mb-1.5" style={{ color: banner.accent, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
            {banner.eyebrow}
          </p>
          <p className="display font-extrabold leading-tight text-white" style={{ fontSize: 'clamp(18px, 4.6vw, 26px)', textShadow: '0 1px 2px rgba(0,0,0,0.55)' }}>
            {banner.title}{' '}
            <span style={{ color: banner.accent, textShadow: `0 0 14px ${banner.accent}88` }}>{banner.highlight}</span>
          </p>
          <p className="text-xs sm:text-sm mt-1.5 leading-snug line-clamp-2" style={{ color: 'rgba(255,255,255,0.85)', textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
            {banner.body}
          </p>
          {banner.showPayments && (
            <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="landing-banner-payments">
              <PayChip code="visa"      label="VISA"  bg="#1A1F71" fg="#FFFFFF" />
              <PayChip code="cashapp"   label="$"     bg="#00D632" fg="#FFFFFF" />
              <PayChip code="applepay"  label="Pay" prefixIcon="apple" bg="#000000" fg="#FFFFFF" />
              <PayChip code="googlepay" label="GPay"  bg="#FFFFFF" fg="#202124" />
              <PayChip code="chime"     label="Chime" bg="#1EC677" fg="#FFFFFF" />
              <PayChip code="btc"       label="₿"     bg="#F7931A" fg="#FFFFFF" />
            </div>
          )}
        </div>

        <div className="hidden sm:flex shrink-0 px-3 py-2 rounded-full items-center gap-1.5 text-xs font-bold" style={{ background: 'rgba(0,0,0,0.45)', border: `1px solid ${banner.accent}66`, color: '#fff', backdropFilter: 'blur(6px)' }}>
          <span>{banner.cta}</span>
          <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.6} />
        </div>
      </div>
      <style>{`
        @keyframes landing-sparkle {
          0%, 100% { opacity: 0.4; transform: scale(0.8); }
          50%      { opacity: 0.95; transform: scale(1.4); }
        }
        @keyframes landing-shimmer {
          0%, 100% { transform: translateX(-110%); }
          50%      { transform: translateX(110%); }
        }
      `}</style>
    </button>
  );
}

// ─────────────────────────── Support overlay ───────────────────────────
function SupportOverlay({ support, brandColor, brandColor2, authPath, onClose }) {
  const channels = [
    { key: 'livechat', label: 'Live Chat', href: authPath, color: brandColor, icon: MessageCircle, sameWindow: true },
    support.messenger_url && { key: 'messenger', label: 'Messenger', href: support.messenger_url, color: '#3B82F6', icon: MessageCircle },
    support.whatsapp_url && { key: 'whatsapp', label: 'WhatsApp', href: support.whatsapp_url, color: '#22c55e', icon: MessageCircle },
    support.telegram_url && { key: 'telegram', label: 'Telegram', href: support.telegram_url, color: '#38BDF8', icon: Send },
    support.facebook_url && { key: 'facebook', label: 'Facebook', href: support.facebook_url, color: '#1877F2', icon: Facebook },
    support.discord_url && { key: 'discord', label: 'Discord', href: support.discord_url, color: '#6366F1', icon: MessageCircle },
    support.email && { key: 'email', label: support.email, href: `mailto:${support.email}`, color: '#EAB308', icon: Mail, sameWindow: true },
    support.phone && { key: 'phone', label: support.phone, href: `tel:${support.phone}`, color: '#A855F7', icon: Phone, sameWindow: true },
  ].filter(Boolean);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center overflow-hidden bg-black/70 p-0 backdrop-blur sm:items-center sm:p-4"
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
      onClick={onClose}
      data-testid="landing-support-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex w-full flex-col overflow-hidden rounded-t-3xl sm:max-w-md sm:rounded-3xl"
        style={{
          maxHeight: 'calc(100dvh - env(safe-area-inset-top) - env(safe-area-inset-bottom))',
          background: '#0F1420',
          border: `1px solid ${brandColor}33`,
          boxShadow: '0 -10px 40px rgba(0,0,0,0.6)',
        }}
      >
        <div className="flex shrink-0 items-center justify-between gap-3 px-4 py-3 sm:px-5 sm:py-4" style={{ background: `linear-gradient(135deg, ${brandColor}, ${brandColor2})` }}>
          <p className="min-w-0 font-extrabold text-black">Need help? Reach us 24/7</p>
          <button onClick={onClose} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-black/15 text-black hover:bg-black/25" aria-label="Close support options" data-testid="landing-support-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="grid min-h-0 flex-1 grid-cols-2 gap-2.5 overflow-y-auto overscroll-contain p-4">
          {channels.map((c) => (
            <a key={c.key} href={c.href} target={!c.sameWindow && c.href.startsWith('http') ? '_blank' : undefined} rel="noreferrer" className="flex items-center gap-2 rounded-xl p-3 transition hover:scale-[1.02]" style={{ background: `${c.color}14`, border: `1px solid ${c.color}44`, color: '#fff' }} data-testid={`landing-support-${c.key}`}>
              <c.icon className="w-5 h-5" style={{ color: c.color }} />
              <span className="text-xs truncate">{c.label}</span>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

function BrandSocialOverlay({ brandName, brandLogo, brandColor, brandColor2, branding, resolveUrl, onClose }) {
  const socials = branding?.socials || {};
  const socialVisibility = branding?.social_visibility || {};
  const entries = SOCIAL_DEFS
    .map(def => ({ ...def, url: normalizeLink(socials[def.key], def.key) }))
    .filter(item => socialVisibility[item.key] !== false && item.url);
  const supportEmail = socials.support_email;
  const verified = branding?.verified !== false;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center overflow-hidden bg-black/70 p-0 backdrop-blur sm:items-center sm:p-4"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
      onClick={onClose}
      data-testid="landing-brand-social-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="player-app flex w-full flex-col overflow-hidden rounded-t-3xl sm:max-w-md sm:rounded-3xl"
        style={{
          maxHeight: 'calc(100dvh - env(safe-area-inset-top) - env(safe-area-inset-bottom))',
          background: 'var(--vl-bg, linear-gradient(180deg, #13283A, #050B1C))',
          border: `1px solid ${brandColor}55`,
          boxShadow: `0 -14px 44px rgba(0,0,0,0.62), 0 0 44px ${brandColor}22`,
        }}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-4" style={{ background: 'rgba(5,11,28,0.92)', borderBottom: `1px solid ${brandColor}40`, backdropFilter: 'blur(14px)' }}>
          <div className="min-w-0">
            <p className="text-lg font-extrabold text-white truncate">{brandName}</p>
            <p className="text-xs text-white/55">Official brand profile</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full flex items-center justify-center text-white active:scale-95"
            style={{ background: 'rgba(255,255,255,0.07)', border: `1px solid ${brandColor}55` }}
            data-testid="landing-brand-social-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:p-5">
          <div className="flex flex-col items-center text-center pb-6">
            <div
              className="w-40 h-40 rounded-full flex items-center justify-center overflow-hidden mb-5 border"
              style={{
                background: 'rgba(255,255,255,0.03)',
                borderColor: brandColor2,
                boxShadow: `0 18px 50px rgba(0,0,0,0.48), 0 0 32px ${brandColor}44`,
              }}
            >
              {brandLogo
                ? (
                  <SafeImage
                    src={resolveUrl(brandLogo)}
                    alt={brandName}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    fallback={<Crown className="w-16 h-16 text-white/75" />}
                  />
                )
                : <Crown className="w-16 h-16 text-white/75" />}
            </div>
            <h2 className="display max-w-full break-words text-xl font-extrabold uppercase tracking-[0.08em] text-white [overflow-wrap:anywhere] sm:text-2xl sm:tracking-[0.14em]">{brandName}</h2>
            {verified && (
              <div className="inline-flex items-center gap-1.5 mt-3 px-3 py-1 rounded-full" style={{ background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.5)' }}>
                <BadgeCheck className="w-4 h-4 text-blue-400" />
                <span className="text-xs font-bold text-blue-300">Official Brand</span>
              </div>
            )}
            {branding?.description && (
              <p className="text-sm text-white/60 mt-4 leading-relaxed">{branding.description}</p>
            )}
          </div>

          {(branding?.stats?.followers || branding?.stats?.games || branding?.stats?.rewards_claimed) && (
            <div
              className="mb-6 rounded-2xl p-4 grid grid-cols-3 gap-2"
              style={{ background: `${brandColor}14`, border: `1px solid ${brandColor}45` }}
              data-testid="landing-brand-social-stats"
            >
              <BrandSocialStat icon={Users} color={brandColor2} value={branding.stats?.followers} label="Followers" />
              <BrandSocialStat icon={Trophy} color={brandColor2} value={branding.stats?.games} label="Games" />
              <BrandSocialStat icon={Gem} color={brandColor2} value={branding.stats?.rewards_claimed} label="Rewards Claimed" />
            </div>
          )}

          <h3 className="flex items-center gap-2 text-white/80 font-bold mb-3">
            <Link2 className="w-4 h-4" /> Connect With Us
          </h3>
          <div className="space-y-2">
            {entries.map(item => <BrandSocialRow key={item.key} item={item} brandName={brandName} />)}
            {entries.length === 0 && !supportEmail && (
              <p className="text-sm text-white/40 text-center py-4">No social links configured yet.</p>
            )}
          </div>

          {supportEmail && (
            <a
              href={`mailto:${supportEmail}`}
              className="mt-5 flex items-center gap-3 rounded-2xl p-4 transition active:scale-[0.99] hover:brightness-110"
              style={{ background: `${brandColor}18`, border: `1px solid ${brandColor}55` }}
              data-testid="landing-brand-social-support-email"
            >
              <span className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${brandColor}, ${brandColor2})` }}>
                <Mail className="w-5 h-5 text-black" />
              </span>
              <div className="flex-1 min-w-0 text-left">
                <p className="font-bold text-white">Need Help?</p>
                <p className="text-xs text-white/70 truncate">{supportEmail}</p>
              </div>
              <span className="shrink-0 text-xs font-bold px-3 py-1.5 rounded-full" style={{ color: brandColor2, border: `1px solid ${brandColor}88`, background: 'rgba(0,0,0,0.25)' }}>Email</span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function BrandSocialStat({ icon: Icon, color, value, label }) {
  if (!value) return <div />;
  return (
    <div className="flex flex-col items-center text-center">
      <Icon className="w-5 h-5 mb-1" style={{ color }} />
      <p className="display font-extrabold text-lg text-white leading-tight">{value}</p>
      <p className="text-[9px] uppercase tracking-wider text-white/60 mt-0.5">{label}</p>
    </div>
  );
}

function BrandSocialRow({ item, brandName }) {
  const Icon = item.icon;
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 rounded-2xl p-3 transition active:scale-[0.99] hover:brightness-110"
      style={{ background: 'rgba(255,255,255,0.035)', border: `1px solid ${item.color}55` }}
      data-testid={`landing-brand-social-${item.key}`}
    >
      <span className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: item.bg, boxShadow: `0 4px 14px ${item.color}55` }}>
        {Icon ? <Icon className="w-5 h-5 text-white" /> : <span className="text-white font-extrabold text-sm">♪</span>}
      </span>
      <div className="flex-1 min-w-0 text-left">
        <p className="font-bold text-white">{item.label}</p>
        <p className="text-xs text-white/60 truncate">{String(item.url).replace(/^https?:\/\//, '') || brandName}</p>
      </div>
      <span className="shrink-0 text-xs font-bold px-3 py-1.5 rounded-full" style={{ color: item.color, border: `1px solid ${item.color}99`, background: 'rgba(0,0,0,0.25)' }}>
        {item.cta}
      </span>
      <ChevronRight className="w-4 h-4 text-white/45 shrink-0" />
    </a>
  );
}

// Compact payment-method pill — used inside the deposit-bonus banner.
function PayChip({ code, label, bg, fg, prefixIcon }) {
  return (
    <span
      className="inline-flex items-center justify-center gap-1 rounded-md font-extrabold tracking-tight select-none"
      style={{
        background: bg,
        color: fg,
        height: 22,
        minWidth: 38,
        padding: '0 8px',
        fontSize: 11,
        boxShadow: '0 2px 6px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.18)',
        border: bg === '#FFFFFF' ? '1px solid rgba(0,0,0,0.08)' : '1px solid rgba(255,255,255,0.12)',
      }}
      title={code}
      data-testid={`landing-pay-${code}`}
    >
      {prefixIcon === 'apple' && (
        <svg viewBox="0 0 24 24" width={11} height={11} fill={fg} aria-hidden>
          <path d="M17.7 12.4c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.2-2.8.9-3.5.9-.7 0-1.8-.8-3-.8-1.5 0-2.9.9-3.7 2.3C4.6 12.4 5.5 17 7 19.3c.7 1.1 1.6 2.4 2.8 2.3 1.1 0 1.5-.7 2.9-.7s1.7.7 2.9.7c1.2 0 2-1.1 2.7-2.2.9-1.3 1.2-2.5 1.2-2.6-.1 0-2.4-1-2.4-3.7l.6.3zM15.5 5.5c.6-.7 1-1.7.9-2.7-1 0-2.1.6-2.8 1.4-.6.7-1.1 1.7-.9 2.7 1 .1 2.1-.6 2.8-1.4z" />
        </svg>
      )}
      <span>{label}</span>
    </span>
  );
}
